import { useState, useEffect } from 'react';
import { HardDrive, Download, Clock, FileArchive, Loader2, RefreshCw, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { adminToolsService } from '../../services';
import { toast } from 'sonner';

export const BackupPanel = () => {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backingUp, setBackingUp] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusData, historyData] = await Promise.all([
        adminToolsService.getBackupStatus(),
        adminToolsService.getBackupHistory()
      ]);
      setStatus(statusData);
      setHistory(historyData.history || []);
    } catch (e) {
      toast.error('Fehler beim Laden der Backup-Informationen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const downloadBackup = async (type) => {
    setBackingUp(type);
    try {
      const response = type === 'full'
        ? await adminToolsService.createFullBackup()
        : await adminToolsService.createIncrementalBackup();

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers['content-disposition'];
      const filename = disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '')
        : `backup_${type}_${new Date().toISOString().slice(0, 10)}.zip`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`${type === 'full' ? 'Voll' : 'Inkrementelles'}-Backup erfolgreich erstellt und heruntergeladen!`);
      loadData();
    } catch (e) {
      toast.error(`Fehler beim Erstellen des Backups: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBackingUp(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-primary)' }} /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HardDrive className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Datensicherung</h3>
        </div>
        <Button size="sm" variant="outline" onClick={loadData} disabled={loading}>
          <RefreshCw className="w-3 h-3 mr-1" /> Aktualisieren
        </Button>
      </div>

      {/* Overview */}
      <div className="grid grid-cols-3 gap-4">
        <Card style={{ borderColor: 'var(--color-border)' }}>
          <CardContent className="pt-6">
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Collections</p>
            <p className="text-2xl font-bold mt-1" style={{ color: 'var(--color-text-primary)' }}>{status?.total_collections || 0}</p>
          </CardContent>
        </Card>
        <Card style={{ borderColor: 'var(--color-border)' }}>
          <CardContent className="pt-6">
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Dokumente</p>
            <p className="text-2xl font-bold mt-1" style={{ color: 'var(--color-text-primary)' }}>{status?.total_documents || 0}</p>
          </CardContent>
        </Card>
        <Card style={{ borderColor: 'var(--color-border)' }}>
          <CardContent className="pt-6">
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Letztes Backup</p>
            <p className="text-sm font-medium mt-1" style={{ color: 'var(--color-text-primary)' }}>
              {status?.last_backup?.created_at
                ? new Date(status.last_backup.created_at).toLocaleString('de-DE')
                : 'Noch keins'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Backup Actions */}
      <div className="grid grid-cols-2 gap-4">
        <Card style={{ borderColor: 'var(--color-border)' }}>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <FileArchive className="w-4 h-4" /> Voll-Backup
            </CardTitle>
            <CardDescription style={{ color: 'var(--color-text-secondary)' }}>
              Exportiert alle Collections und Dokumente als ZIP-Datei
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => downloadBackup('full')}
              disabled={backingUp !== null}
              className="w-full"
              style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-on-primary)' }}
              data-testid="full-backup-btn"
            >
              {backingUp === 'full' ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Backup wird erstellt...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Voll-Backup herunterladen</>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card style={{ borderColor: 'var(--color-border)' }}>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <Clock className="w-4 h-4" /> Inkrementelles Backup
            </CardTitle>
            <CardDescription style={{ color: 'var(--color-text-secondary)' }}>
              Nur geänderte Daten seit dem letzten Backup
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => downloadBackup('incremental')}
              disabled={backingUp !== null}
              className="w-full"
              variant="outline"
              style={{ borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}
              data-testid="incremental-backup-btn"
            >
              {backingUp === 'incremental' ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Backup wird erstellt...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Inkrementelles Backup</>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Collection Details */}
      <Card style={{ borderColor: 'var(--color-border)' }}>
        <CardHeader>
          <CardTitle className="text-base" style={{ color: 'var(--color-text-primary)' }}>Collections im Backup</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2">
            {status?.collections?.map(c => (
              <div key={c.name} className="flex items-center justify-between p-2 rounded" style={{ backgroundColor: 'var(--color-background)' }}>
                <span className="text-sm font-mono" style={{ color: 'var(--color-text-primary)' }}>{c.name}</span>
                <Badge variant="outline" className="text-xs">{c.document_count} Docs</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Backup History */}
      {history.length > 0 && (
        <Card style={{ borderColor: 'var(--color-border)' }}>
          <CardHeader>
            <CardTitle className="text-base" style={{ color: 'var(--color-text-primary)' }}>Backup-Verlauf</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: 'var(--color-background)' }} className="border-b">
                  <th className="text-left p-3 font-medium" style={{ color: 'var(--color-text-primary)' }}>Zeitpunkt</th>
                  <th className="text-left p-3 font-medium" style={{ color: 'var(--color-text-primary)' }}>Typ</th>
                  <th className="text-left p-3 font-medium" style={{ color: 'var(--color-text-primary)' }}>Erstellt von</th>
                  <th className="text-left p-3 font-medium" style={{ color: 'var(--color-text-primary)' }}>Collections</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i} className="border-b">
                    <td className="p-3">{h.created_at ? new Date(h.created_at).toLocaleString('de-DE') : '-'}</td>
                    <td className="p-3">
                      <Badge variant={h.type === 'full' ? 'default' : 'secondary'} className="text-xs">
                        {h.type === 'full' ? 'Voll' : 'Inkrementell'}
                      </Badge>
                    </td>
                    <td className="p-3">{h.created_by || '-'}</td>
                    <td className="p-3">{h.collections?.length || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
